import { useState, useRef, useEffect, useMemo } from 'react';
import { GoogleGenAI } from '@google/genai';
import './Chatbot.css';

const SYSTEM_PROMPT = `You are a helpful assistant for Gitsawe (ግጻዌ), an Ethiopian Orthodox Church application.

## About the Developer
When asked about the developer, creator, or "who made this app", respond with:
- **Name:** Hailemariam Eyayu (ኃይለማርያም እያዩ)
- **Role:** Full-Stack አልሚ/Developer & Creator of E-Gitsawe and Sinksar
- **Location:** Addis Ababa, Ethiopia
- **Contact:** hailemariameyayu2012@gmail.com | +251-938169557
- **Telegram:** @Hailemariam_SWE
- **LinkedIn:** linkedin.com/in/hailemariam-eyayu
- **Mission:** Building digital tools to preserve and share Ethiopian Orthodox Tewahedo Church teachings and liturgical resources
- He is a devoted Ethiopian Orthodox Christian አልሚ/developer who built this platform to make church resources accessible to everyone digitally.

## Your Role
Help users with:
- Information about Ethiopian Orthodox traditions, prayers, and teachings
- Guidance on using the Gitsawe app features
- Explanations about Ge'ez liturgical texts and Mahlet (ማኅሌት)
- Ethiopian Orthodox calendar and fasting periods
- Saints, holidays, and church history

Always be respectful, knowledgeable, and provide accurate information about Ethiopian Orthodox Christianity.
Respond in both Amharic and English based on the user's language preference.`;

function Chatbot() {
  const [messages, setMessages] = useState(() => {
    const saved = localStorage.getItem('chatbot-messages');
    return saved ? JSON.parse(saved) : [];
  });
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const lastMessageTime = useRef(0);

  const genAI = useMemo(() => {
    const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
    return apiKey ? new GoogleGenAI({ apiKey }) : null;
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    localStorage.setItem('chatbot-messages', JSON.stringify(messages));
  }, [messages]);

  const clearChat = () => {
    if (window.confirm('ውይይቱን መሰረዝ ይፈልጋሉ? / Clear all messages?')) {
      setMessages([]);
      localStorage.removeItem('chatbot-messages');
    }
  };

  const sendMessage = async () => {
    if (!input.trim()) return;

    // Rate limiting: prevent spam (1 message per 2 seconds)
    const now = Date.now();
    if (now - lastMessageTime.current < 2000) {
      return;
    }
    lastMessageTime.current = now;

    const userMessage = { 
      role: 'user', 
      content: input,
      timestamp: new Date().toISOString()
    };
    const currentInput = input;
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      if (!genAI) {
        throw new Error('API key not found');
      }

      // Build conversation with history
      const conversationHistory = messages.map((msg) => ({
        role: msg.role === 'user' ? 'user' : 'model',
        parts: [{ text: msg.content }]
      }));

      const contents = [
        {
          role: 'user',
          parts: [{ text: SYSTEM_PROMPT }]
        },
        {
          role: 'model',
          parts: [{ text: 'I understand. I will help with Ethiopian Orthodox Church information and Gitsawe app features in Amharic and English.' }]
        },
        ...conversationHistory,
        {
          role: 'user',
          parts: [{ text: currentInput }]
        }
      ];

      const response = await genAI.models.generateContent({
        model: 'gemini-2.5-flash',
        contents: contents
      });

      const text = response.text || 'No response generated';

      const botMessage = { 
        role: 'bot', 
        content: text,
        timestamp: new Date().toISOString()
      };
      setMessages((prev) => [...prev, botMessage]);
    } catch (error) {
      let errorMsg = 'ይቅርታ፣ ስህተት ተፈጥሯል። እባክዎ እንደገና ይሞክሩ።';
      
      if (error.message?.includes('quota') || error.message?.includes('RESOURCE_EXHAUSTED')) {
        errorMsg = 'API quota exceeded. Please wait a moment and try again.';
      } else if (error.message?.includes('404') || error.message?.includes('NOT_FOUND')) {
        errorMsg = 'Model not available. Please check your API configuration.';
      } else if (error.message) {
        errorMsg = `Error: ${error.message}`;
      }
      
      const errorMessage = {
        role: 'bot',
        content: errorMsg,
        timestamp: new Date().toISOString()
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const copyMessage = (content) => {
    navigator.clipboard.writeText(content);
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      <button className="chatbot-toggle" onClick={() => setIsOpen(!isOpen)}>
        {isOpen ? '✕' : '💬'}
      </button>

      {isOpen && (
        <div className="chatbot-container">
          <div className="chatbot-header">
            <h3>🤖 ግጻዌ AI</h3>
            <div className="header-actions">
              {messages.length > 0 && (
                <button onClick={clearChat} className="clear-btn" title="Clear chat">
                  🗑️
                </button>
              )}
              <button onClick={() => setIsOpen(false)} className="close-btn">
                ✕
              </button>
            </div>
          </div>

          <div className="chatbot-messages">
            {messages.length === 0 && (
              <div className="welcome-message">
                <p>👋 እንኳን ደህና መጡ!</p>
                <p>ስለ ግጻዌ፣ ሥርዓተ ማኅሌት፣ ወይም ሌላ ጥያቄ ይጠይቁ።</p>
                <div className="suggestions">
                  <button onClick={() => setInput('ስለ ግጻዌ ንገረኝ')}>ስለ ግጻዌ ንገረኝ</button>
                  <button onClick={() => setInput('የዛሬ የጾም ቀን ነው?')}>የዛሬ የጾም ቀን ነው?</button>
                  <button onClick={() => setInput('Tell me about Ethiopian Orthodox fasting')}>About Fasting</button>
                </div>
              </div>
            )}
            {messages.map((msg, index) => (
              <div key={index} className={`message ${msg.role}`}>
                <div className="message-content">
                  {msg.content}
                  {msg.role === 'bot' && (
                    <button 
                      className="copy-btn" 
                      onClick={() => copyMessage(msg.content)}
                      title="Copy message"
                    >
                      📋
                    </button>
                  )}
                </div>
                {msg.timestamp && (
                  <div className="message-time">{formatTime(msg.timestamp)}</div>
                )}
              </div>
            ))}
            {isLoading && (
              <div className="message bot">
                <div className="message-content loading">
                  <span className="dot"></span>
                  <span className="dot"></span>
                  <span className="dot"></span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="chatbot-input">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="ጥያቄዎን እዚህ ይጻፉ..."
              rows="2"
            />
            <button onClick={sendMessage} disabled={isLoading || !input.trim()}>
              ላክ
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default Chatbot;
